import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useRef } from 'react';
import { ModalWindow, Spinner, Checkbox } from 'libs.nucleus';
import { ReportAnalyticsEvents } from '../../../constants/report_analytics_events.constants';
import { usePaginatedCubeQuery } from '../../../hooks/use_paginated_cube_query';
import { processResultSet } from '../../../utils/data_reports.utils';
import { createCombinedResultSetForPivot } from '../../../utils/pagination_pivot.utils';
export const ChartQueryFilter = ({ isOpen, onClose, applyFilters, filterData, initialSelectedOptions, logReportEvent, }) => {
    const [isLoadingResultSet, setIsLoadingResultSet] = useState(true);
    const filterLoadLoggedRef = useRef(false);
    // Memoize query object to prevent infinite re-renders
    const query = useMemo(() => {
        return { ...filterData.query, renewQuery: true, total: true };
    }, [filterData.query]);
    const { resultSets, isLoading, error } = usePaginatedCubeQuery(query, {
        skip: !isLoadingResultSet,
        onQueryExecuted: () => {
            logReportEvent?.(ReportAnalyticsEvents.QUERY_EXECUTED, {
                query_source: 'filter',
                filter_key: filterData.key,
            });
        },
    });
    // Analytics: chart filter load lifecycle
    useEffect(() => {
        logReportEvent?.(ReportAnalyticsEvents.FILTER_LOAD_STARTED, {
            filter_key: filterData.key,
            filter_source: 'chart_query',
        });
        filterLoadLoggedRef.current = false;
    }, [filterData.key]);
    useEffect(() => {
        if (filterLoadLoggedRef.current || isLoading) {
            return;
        }
        if (error) {
            filterLoadLoggedRef.current = true;
            logReportEvent?.(ReportAnalyticsEvents.FILTER_LOAD_FAILED, {
                filter_key: filterData.key,
                filter_source: 'chart_query',
                error_message: error.message,
            });
        }
        else if (resultSets && resultSets.length > 0) {
            filterLoadLoggedRef.current = true;
            logReportEvent?.(ReportAnalyticsEvents.FILTER_LOAD_COMPLETED, {
                filter_key: filterData.key,
                filter_source: 'chart_query',
            });
        }
    }, [isLoading, error, resultSets]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions || []);
    useEffect(() => {
        if (resultSets && resultSets.length > 0) {
            // Use our custom combined ResultSet that handles pagination correctly
            const combinedResultSet = createCombinedResultSetForPivot(resultSets);
            const options = processResultSet(combinedResultSet, filterData.key);
            setOptions(options.filter((option) => option !== null));
        }
    }, [resultSets, filterData.key]);
    useEffect(() => {
        if (resultSets || error) {
            setIsLoadingResultSet(false);
        }
    }, [resultSets, error]);
    const savePrimaryButton = {
        label: 'Apply',
        onClick: () => applyFilters({ selectedOptions, queryKey: filterData.key }),
    };
    const handleSelect = (checked, option) => {
        if (checked) {
            setSelectedOptions([...selectedOptions, option]);
        }
        else {
            setSelectedOptions(selectedOptions.filter((selectedOption) => selectedOption.value !== option.value));
        }
    };
    const handleToggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedOptions([]);
        }
        else {
            setSelectedOptions(options);
        }
        setIsAllSelected(!isAllSelected);
    };
    return (_jsx(ModalWindow, { title: 'Filter report card', subtitle: 'Here are some filters that can be applied to this card:', isOpen: isOpen, closeWindow: onClose, footerPrimaryActionButton: savePrimaryButton, footerSecondaryActionButtons: [
            {
                label: 'Cancel',
                onClick: onClose,
            },
        ], children: _jsxs("div", { className: 'relative px-4 min-h-[100px]', children: [isLoading && _jsx(Spinner, { wrapper: 'full' }), !isLoading && (!resultSets || resultSets.length === 0 || error) && _jsxs("div", { children: ["No data -- ", error?.message] }), !error && !!options.length && (_jsxs("div", { className: 'space-y-2', children: [_jsx(Checkbox, { label: 'Select all/none', onChange: handleToggleSelectAll, checked: isAllSelected }), options.map((option) => (_jsx(Checkbox, { label: option.label, onChange: (checked) => handleSelect(checked, option), checked: selectedOptions.find((selectedOption) => selectedOption.value === option.value) !== undefined }, option.value)))] }))] }) }));
};
